import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Progress } from "../components/ui/progress";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  Inbox, Settings, RefreshCw, Loader2, Mail, PlugZap, PlayCircle,
  CheckCircle2, Clock, FileText, ShieldCheck, Eye,
} from "lucide-react";

const fmtFecha = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("es-DO", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const statusBadge = (s) => {
  const up = (s || "").toUpperCase();
  const map = {
    "PENDIENTE": "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    "RECIBIDA": "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    "RENOVADA": "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    "CANCELADA X PAGO": "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    "CANCELADA X FALTA DE PAGO": "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
  };
  return map[up] || "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300";
};

const MOTIVO_LABELS = {
  por_pago: "por falta de pago",
  por_cliente: "a solicitud del cliente",
  no_identificado: "motivo no identificado",
};

export default function BandejaEntradaPage() {
  const { user, getAuthHeaders } = useAuth();
  const isAdmin = ["admin", "administrador", "superadmin"].includes((user?.role || "").toLowerCase()) || !!user?.is_admin;

  const [hallazgos, setHallazgos] = useState([]);
  const [reprocesar, setReprocesar] = useState(false);
  const [verAplicados, setVerAplicados] = useState(false);
  // Mostrar/ocultar registros "Solo consulta" (ya no están en PENDIENTE en la plataforma)
  const [verInformativos, setVerInformativos] = useState(() => localStorage.getItem("bandeja_ver_informativos") !== "0");
  const toggleInformativos = (v) => {
    setVerInformativos(v);
    localStorage.setItem("bandeja_ver_informativos", v ? "1" : "0");
  };
  const hallazgosVisibles = (!verAplicados && !verInformativos)
    ? hallazgos.filter((h) => h.estado !== "informativo")
    : hallazgos;
  const [loading, setLoading] = useState(true);
  const [ejecutando, setEjecutando] = useState(null);
  const [estatusSel, setEstatusSel] = useState({});
  const [sel, setSel] = useState([]);
  const [ejecutandoLote, setEjecutandoLote] = useState(false);
  const [chequeando, setChequeando] = useState(false);
  const [runActivo, setRunActivo] = useState(null);
  const pollRef = useRef(null);
  const [runs, setRuns] = useState([]);

  const [config, setConfig] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);

  const fetchHallazgos = useCallback(async (aplicados = verAplicados) => {
    try {
      const res = await axios.get(
        `${API}/bandeja-entrada/hallazgos?estado=${aplicados ? "aplicado" : "pendiente"}`,
        getAuthHeaders()
      );
      setHallazgos(res.data || []);
      setSel([]);
    } catch (e) {
      toast.error("Error cargando hallazgos");
    } finally {
      setLoading(false);
    }
  }, [verAplicados, getAuthHeaders]);

  const fetchRuns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bandeja-entrada/runs`, getAuthHeaders());
      setRuns(res.data || []);
    } catch { /* silencioso */ }
  }, [getAuthHeaders]);

  const fetchConfig = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await axios.get(`${API}/bandeja-entrada/config`, getAuthHeaders());
      setConfig(res.data);
    } catch { /* silencioso */ }
  }, [isAdmin, getAuthHeaders]);

  useEffect(() => {
    fetchHallazgos();
    fetchRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAdmin && !config) fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const toggleAplicados = (v) => {
    setVerAplicados(v);
    setLoading(true);
    fetchHallazgos(v);
  };

  const ejecutar = async (h) => {
    const estatus = (h.tipo === "cancelacion" ? (estatusSel[h.id] || h.estatus_propuesto) : h.estatus_propuesto) || "RECIBIDA";
    if (!window.confirm(`¿Autorizar el cambio de la póliza ${h.poliza} de "${h.estatus_actual}" a "${estatus}" en Control de Renovaciones?`)) return;
    setEjecutando(h.id);
    try {
      const res = await axios.post(`${API}/bandeja-entrada/hallazgos/${h.id}/ejecutar`, { estatus }, getAuthHeaders());
      toast.success(res.data?.message || "Actualizado");
      fetchHallazgos();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al ejecutar");
    } finally {
      setEjecutando(null);
    }
  };

  const ejecutarLote = async () => {
    if (sel.length === 0) return;
    if (!window.confirm(`¿Autorizar la actualización de ${sel.length} póliza(s) seleccionada(s) en Control de Renovaciones?`)) return;
    setEjecutandoLote(true);
    try {
      const items = sel.map((id) => {
        const h = hallazgos.find((x) => x.id === id);
        return { id, estatus: h?.tipo === "cancelacion" ? (estatusSel[id] || h.estatus_propuesto) : undefined };
      });
      const res = await axios.post(`${API}/bandeja-entrada/hallazgos/ejecutar-lote`, { items }, getAuthHeaders());
      const { aplicados = [], errores = [] } = res.data || {};
      if (errores.length === 0) {
        toast.success(`${aplicados.length} póliza(s) actualizada(s) correctamente`);
      } else {
        toast.warning(`${aplicados.length} actualizada(s), ${errores.length} con error: ${errores[0]?.error || ""}`);
      }
      fetchHallazgos();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al ejecutar el lote");
    } finally {
      setEjecutandoLote(false);
    }
  };

  const chequearAhora = async () => {
    setChequeando(true);
    try {
      const res = await axios.post(`${API}/bandeja-entrada/chequear-ahora?reprocesar=${reprocesar}`, {}, getAuthHeaders());
      toast.success(res.data?.message || "Chequeo iniciado");
      iniciarPolling();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error iniciando el chequeo");
      setChequeando(false);
    }
  };

  const iniciarPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/bandeja-entrada/chequeo-estado`, getAuthHeaders());
        const run = res.data || {};
        setRunActivo(run);
        if (run.estado && run.estado !== "en_proceso") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setChequeando(false);
          fetchHallazgos();
          fetchRuns();
          if (run.estado === "completado") {
            const info = run.hallazgos_informativos ? ` y ${run.hallazgos_informativos} de consulta` : "";
            toast.success(`Chequeo completado: ${run.correos_revisados || 0} correo(s) revisados, ${run.hallazgos_nuevos || 0} hallazgo(s) nuevo(s)${info}`);
          } else {
            toast.error(`El chequeo terminó con error: ${(run.errores || [])[0] || "revise la configuración"}`);
          }
          setTimeout(() => setRunActivo(null), 8000);
        }
      } catch { /* silencioso, reintenta en el próximo tick */ }
    }, 2500);
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const guardarConfig = async () => {
    setGuardando(true);
    try {
      await axios.put(`${API}/bandeja-entrada/config`, config, getAuthHeaders()).then((res) => {
        toast.success(res.data?.message || "Configuración guardada");
      });
      fetchConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando configuración");
    } finally {
      setGuardando(false);
    }
  };

  const probarConexion = async () => {
    setProbando(true);
    try {
      const res = await axios.post(`${API}/bandeja-entrada/probar-conexion`, {}, getAuthHeaders());
      toast.success(res.data?.message || "Conexión exitosa");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo conectar");
    } finally {
      setProbando(false);
    }
  };

  const ultimaRun = runs[0];

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="ml-0 md:ml-64 flex-1 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="bandeja-entrada-page">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white font-['Manrope'] flex items-center gap-2">
                <Inbox className="w-7 h-7 text-blue-600" />
                Bandeja de Entrada
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Pólizas detectadas en el correo que coinciden con Control de Renovaciones
              </p>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 cursor-pointer select-none">
                  <Checkbox
                    checked={reprocesar}
                    onCheckedChange={(v) => setReprocesar(!!v)}
                    data-testid="reprocesar-checkbox"
                  />
                  Releer correos ya procesados
                </label>
                <Button onClick={chequearAhora} disabled={chequeando} className="bg-blue-600 hover:bg-blue-700" data-testid="chequear-ahora-btn">
                  {chequeando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  Chequear correo ahora
                </Button>
              </div>
            )}
          </div>

          {runActivo && (
            <Card className="mb-4 border-blue-200 dark:border-blue-800" data-testid="progreso-chequeo">
              <CardContent className="py-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 font-medium text-slate-700 dark:text-slate-200">
                    {runActivo.estado === "en_proceso" ? (
                      <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    )}
                    {runActivo.fase || (runActivo.estado === "en_proceso" ? "Procesando..." : "Finalizado")}
                  </span>
                  <span className="text-xs text-slate-500" data-testid="progreso-contador">
                    {runActivo.progreso_total > 0
                      ? `Correo ${Math.min(runActivo.progreso_actual + (runActivo.estado === "en_proceso" ? 1 : 0), runActivo.progreso_total)} de ${runActivo.progreso_total}`
                      : ""}
                  </span>
                </div>
                <Progress
                  value={runActivo.progreso_total > 0 ? Math.round((runActivo.progreso_actual / runActivo.progreso_total) * 100) : (runActivo.estado === "en_proceso" ? 5 : 100)}
                  className="h-2"
                />
                <div className="flex gap-4 text-xs text-slate-500">
                  <span>Correos nuevos: {runActivo.correos_nuevos || 0}</span>
                  <span>PDFs: {runActivo.adjuntos_pdf || 0}</span>
                  <span className="text-blue-600 font-medium">Hallazgos: {runActivo.hallazgos_nuevos || 0}</span>
                  {(runActivo.hallazgos_informativos || 0) > 0 && (
                    <span>Solo consulta: {runActivo.hallazgos_informativos}</span>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <Tabs defaultValue="hallazgos">
            <TabsList className="mb-4">
              <TabsTrigger value="hallazgos" data-testid="tab-hallazgos">
                <ShieldCheck className="w-4 h-4 mr-1.5" />
                Renovaciones Encontradas
              </TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="config" data-testid="tab-config">
                  <Settings className="w-4 h-4 mr-1.5" />
                  Configuración
                </TabsTrigger>
              )}
            </TabsList>

            {/* ===== PESTAÑA HALLAZGOS ===== */}
            <TabsContent value="hallazgos">
              <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
                <CardHeader className="pb-3 border-b border-slate-100 dark:border-slate-700">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <CardTitle className="text-base font-semibold text-slate-900 dark:text-white">
                        {verAplicados ? "Hallazgos aplicados" : "Pendientes de autorización"} ({hallazgosVisibles.length})
                      </CardTitle>
                      <CardDescription className="text-xs dark:text-slate-400">
                        {ultimaRun ? `Último chequeo: ${fmtFecha(ultimaRun.inicio)} · ${ultimaRun.correos_nuevos ?? 0} correos nuevos · ${ultimaRun.hallazgos_nuevos ?? 0} hallazgos` : "Aún no se ha ejecutado ningún chequeo"}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {!verAplicados && sel.length > 0 && (
                        <Button
                          size="sm"
                          onClick={ejecutarLote}
                          disabled={ejecutandoLote}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid="ejecutar-lote-btn"
                        >
                          {ejecutandoLote ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <PlayCircle className="w-3.5 h-3.5 mr-1" />}
                          Ejecutar seleccionadas ({sel.length})
                        </Button>
                      )}
                      {!verAplicados && (
                        <>
                          <span className="text-xs text-slate-500 dark:text-slate-400">Ver solo consulta</span>
                          <Switch checked={verInformativos} onCheckedChange={toggleInformativos} data-testid="ver-informativos-switch" />
                        </>
                      )}
                      <span className="text-xs text-slate-500 dark:text-slate-400">Ver aplicados</span>
                      <Switch checked={verAplicados} onCheckedChange={toggleAplicados} data-testid="ver-aplicados-switch" />
                      <Button variant="ghost" size="sm" onClick={() => { setLoading(true); fetchHallazgos(); fetchRuns(); }} data-testid="refrescar-hallazgos-btn">
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
                  ) : hallazgosVisibles.length === 0 ? (
                    <div className="p-10 text-center text-slate-400" data-testid="hallazgos-vacio">
                      <Inbox className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">{verAplicados ? "No hay hallazgos aplicados todavía" : (!verInformativos && hallazgos.length > 0 ? "Solo hay registros de consulta (ocultos). Activá \"Ver solo consulta\" para verlos." : "No hay renovaciones pendientes de autorizar")}</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                      <table className="w-full text-sm" data-testid="hallazgos-table">
                        <thead className="sticky top-0 z-10">
                          <tr className="bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-b border-slate-100 dark:border-slate-700 shadow-sm">
                            {!verAplicados && (
                              <th className="px-3 py-2.5 w-8">
                                <Checkbox
                                  checked={hallazgos.filter((h) => h.estado !== "informativo").length > 0 && sel.length === hallazgos.filter((h) => h.estado !== "informativo").length}
                                  onCheckedChange={(v) => setSel(v ? hallazgos.filter((h) => h.estado !== "informativo").map((h) => h.id) : [])}
                                  data-testid="seleccionar-todos-checkbox"
                                />
                              </th>
                            )}
                            <th className="text-left px-4 py-2.5 font-medium">Póliza</th>
                            <th className="text-left px-3 py-2.5 font-medium">Cliente</th>
                            <th className="text-left px-3 py-2.5 font-medium">Compañía</th>
                            <th className="text-center px-3 py-2.5 font-medium">Tipo</th>
                            <th className="text-center px-3 py-2.5 font-medium">Estatus actual</th>
                            <th className="text-center px-3 py-2.5 font-medium">Propuesto</th>
                            <th className="text-left px-3 py-2.5 font-medium">Nota (correo)</th>
                            <th className="text-center px-3 py-2.5 font-medium">Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hallazgosVisibles.map((h) => (
                            <tr key={h.id} className="border-b border-slate-50 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/40" data-testid={`hallazgo-row-${h.id}`}>
                              {!verAplicados && (
                                <td className="px-3 py-3 w-8">
                                  {h.estado === "informativo" ? (
                                    <span className="text-slate-300 dark:text-slate-600">—</span>
                                  ) : (
                                    <Checkbox
                                      checked={sel.includes(h.id)}
                                      onCheckedChange={(v) =>
                                        setSel((prev) => (v ? [...prev, h.id] : prev.filter((x) => x !== h.id)))
                                      }
                                      data-testid={`sel-checkbox-${h.id}`}
                                    />
                                  )}
                                </td>
                              )}
                              <td className="px-4 py-3 font-mono font-medium text-slate-900 dark:text-white whitespace-nowrap">{h.poliza}</td>
                              <td className="px-2 py-3 text-slate-700 dark:text-slate-300 max-w-[150px] truncate" title={h.cliente}>{h.cliente || "-"}</td>
                              <td className="px-2 py-3 text-slate-700 dark:text-slate-300 max-w-[110px] truncate" title={h.compania}>{h.compania || "-"}</td>
                              <td className="px-2 py-3 text-center" data-testid={`tipo-${h.id}`}>
                                {h.tipo === "cancelacion" ? (
                                  <div>
                                    <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">Cancelación</Badge>
                                    <p className="text-[10px] text-slate-400 mt-0.5">{MOTIVO_LABELS[h.motivo_cancelacion] || ""}</p>
                                  </div>
                                ) : (
                                  <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">Renovación</Badge>
                                )}
                              </td>
                              <td className="px-2 py-3 text-center">
                                <Badge className={statusBadge(h.estado === "aplicado" ? (h.estatus_anterior || h.estatus_actual) : h.estatus_actual)} data-testid={`estatus-actual-${h.id}`}>
                                  {(h.estado === "aplicado" ? (h.estatus_anterior || h.estatus_actual) : h.estatus_actual) || "-"}
                                </Badge>
                              </td>
                              <td className="px-2 py-3 text-center">
                                {h.tipo === "cancelacion" && h.estado !== "aplicado" && h.estado !== "informativo" ? (
                                  <select
                                    value={estatusSel[h.id] || h.estatus_propuesto}
                                    onChange={(e) => setEstatusSel({ ...estatusSel, [h.id]: e.target.value })}
                                    className="text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-1.5 py-1"
                                    data-testid={`estatus-select-${h.id}`}
                                  >
                                    <option value="CANCELADA X FALTA DE PAGO">CANCELADA X FALTA DE PAGO</option>
                                  </select>
                                ) : (
                                  <Badge className={statusBadge(h.estatus_aplicado || h.estatus_propuesto)}>{h.estatus_aplicado || h.estatus_propuesto}</Badge>
                                )}
                              </td>
                              <td className="px-2 py-3 max-w-[260px]">
                                <p className="text-slate-700 dark:text-slate-300 truncate" title={h.email_subject}>
                                  <Mail className="w-3 h-3 inline mr-1 text-slate-400" />{h.email_subject || "(sin asunto)"}
                                </p>
                                <p className="text-xs text-slate-400 truncate" title={`De: ${h.email_from} · Para: ${h.email_to}`}>
                                  {fmtFecha(h.email_fecha)} · De: {h.email_from}
                                </p>
                                <p className="text-xs text-slate-400 truncate" title={h.archivo}>
                                  <FileText className="w-3 h-3 inline mr-1" />{h.archivo}
                                </p>
                              </td>
                              <td className="px-2 py-3 text-center whitespace-nowrap">
                                {h.estado === "aplicado" ? (
                                  <div className="text-xs text-green-600 dark:text-green-400">
                                    <CheckCircle2 className="w-4 h-4 inline mr-1" />
                                    Aplicado
                                    <p className="text-[10px] text-slate-400">{h.aplicado_por} · {fmtFecha(h.aplicado_at)}</p>
                                  </div>
                                ) : h.estado === "informativo" ? (
                                  <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300" data-testid={`solo-consulta-${h.id}`}>
                                    <Eye className="w-3 h-3 mr-1" />
                                    Solo consulta
                                  </Badge>
                                ) : (
                                  <Button
                                    size="sm"
                                    onClick={() => ejecutar(h)}
                                    disabled={ejecutando === h.id}
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                    data-testid={`ejecutar-btn-${h.id}`}
                                  >
                                    {ejecutando === h.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <PlayCircle className="w-3.5 h-3.5 mr-1" />}
                                    Ejecutar
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ===== PESTAÑA CONFIGURACIÓN (solo admin) ===== */}
            {isAdmin && (
              <TabsContent value="config">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <Card className="lg:col-span-2 border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
                    <CardHeader>
                      <CardTitle className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                        <Settings className="w-4 h-4 text-blue-600" /> Configuración del correo
                      </CardTitle>
                      <CardDescription className="text-xs dark:text-slate-400">
                        El sistema se conecta en modo solo lectura (no borra ni marca correos)
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {!config ? (
                        <div className="p-6 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
                      ) : (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="md:col-span-2">
                              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Servidor IMAP</label>
                              <Input value={config.imap_host || ""} onChange={(e) => setConfig({ ...config, imap_host: e.target.value })} placeholder="outlook.office365.com / imap.gmail.com" data-testid="config-imap-host" />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Puerto</label>
                              <Input type="number" value={config.imap_port || 993} onChange={(e) => setConfig({ ...config, imap_port: parseInt(e.target.value) || 993 })} data-testid="config-imap-port" />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Método de autenticación</label>
                            <select
                              value={config.auth_metodo || "password"}
                              onChange={(e) => setConfig({ ...config, auth_metodo: e.target.value })}
                              className="w-full mt-1 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-2 py-2"
                              data-testid="config-auth-metodo"
                            >
                              <option value="password">Contraseña (IMAP básico — Gmail y otros)</option>
                              <option value="oauth_microsoft">Microsoft 365 / Office 365 (OAuth — requerido por Microsoft)</option>
                            </select>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Correo / Buzón</label>
                              <Input value={config.email || ""} onChange={(e) => setConfig({ ...config, email: e.target.value })} placeholder="documentos@qasesores.com.do" data-testid="config-email" />
                            </div>
                            {(config.auth_metodo || "password") === "password" && (
                              <div>
                                <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Contraseña (de aplicación)</label>
                                <Input type="password" value={config.password || ""} onChange={(e) => setConfig({ ...config, password: e.target.value })} placeholder="••••••••" data-testid="config-password" />
                              </div>
                            )}
                          </div>
                          {(config.auth_metodo || "password") === "oauth_microsoft" && (
                            <div className="space-y-3 p-3 rounded-lg border border-blue-100 dark:border-blue-900/40 bg-blue-50/50 dark:bg-blue-900/10" data-testid="config-oauth-panel">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Tenant ID (Directorio)</label>
                                  <Input value={config.tenant_id || ""} onChange={(e) => setConfig({ ...config, tenant_id: e.target.value })} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" data-testid="config-tenant-id" />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Client ID (Aplicación)</label>
                                  <Input value={config.client_id || ""} onChange={(e) => setConfig({ ...config, client_id: e.target.value })} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" data-testid="config-client-id" />
                                </div>
                              </div>
                              <div>
                                <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Client Secret (Valor del secreto)</label>
                                <Input type="password" value={config.client_secret || ""} onChange={(e) => setConfig({ ...config, client_secret: e.target.value })} placeholder="••••••••" data-testid="config-client-secret" />
                              </div>
                              <details className="text-xs text-slate-600 dark:text-slate-400">
                                <summary className="cursor-pointer font-medium text-blue-700 dark:text-blue-400" data-testid="oauth-guia-toggle">
                                  ¿Cómo obtener estas credenciales? (guía para el administrador de Microsoft 365)
                                </summary>
                                <ol className="list-decimal ml-4 mt-2 space-y-1">
                                  <li>En <strong>entra.microsoft.com</strong> → Aplicaciones → Registros de aplicaciones → <strong>Nuevo registro</strong> (ej. "QAsesores Bandeja"). Copie el <strong>Id. de aplicación (Client ID)</strong> y el <strong>Id. de directorio (Tenant ID)</strong>.</li>
                                  <li>En Certificados y secretos → <strong>Nuevo secreto de cliente</strong> → copie el <strong>Valor</strong> (Client Secret).</li>
                                  <li>En Permisos de API → Agregar permiso → <strong>APIs que usa mi organización</strong> → "Office 365 Exchange Online" → Permisos de <strong>aplicación</strong> → <strong>IMAP.AccessAsApp</strong> → y presione <strong>Conceder consentimiento de administrador</strong>.</li>
                                  <li>En PowerShell de Exchange Online (con el Object ID de la <strong>aplicación empresarial</strong>, no del registro): <code>New-ServicePrincipal -AppId &lt;ClientID&gt; -ObjectId &lt;ObjectID&gt;</code> y luego <code>Add-MailboxPermission -Identity documentos@qasesores.com.do -User &lt;ServicePrincipal&gt; -AccessRights FullAccess</code>.</li>
                                  <li>Verifique que el buzón tenga IMAP habilitado: <code>Set-CASMailbox documentos@qasesores.com.do -ImapEnabled $true</code>.</li>
                                </ol>
                              </details>
                            </div>
                          )}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Carpeta</label>
                              <Input value={config.carpeta || "INBOX"} onChange={(e) => setConfig({ ...config, carpeta: e.target.value })} data-testid="config-carpeta" />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Hora de corrida diaria</label>
                              <Input type="time" value={config.hora_corrida || "07:00"} onChange={(e) => setConfig({ ...config, hora_corrida: e.target.value })} data-testid="config-hora" />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Días hacia atrás a revisar</label>
                              <Input type="number" min={1} max={30} value={config.dias_iniciales || 7} onChange={(e) => setConfig({ ...config, dias_iniciales: parseInt(e.target.value) || 7 })} data-testid="config-dias" />
                            </div>
                          </div>
                          <div className="flex items-center gap-2 pt-1">
                            <Switch checked={!!config.activo} onCheckedChange={(v) => setConfig({ ...config, activo: v })} data-testid="config-activo-switch" />
                            <span className="text-sm text-slate-700 dark:text-slate-300">Chequeo diario automático activo (a la hora configurada)</span>
                          </div>
                          <div className="flex items-center gap-2 pt-2 flex-wrap">
                            <Button onClick={guardarConfig} disabled={guardando} className="bg-blue-600 hover:bg-blue-700" data-testid="config-guardar-btn">
                              {guardando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                              Guardar configuración
                            </Button>
                            <Button variant="outline" onClick={probarConexion} disabled={probando} data-testid="config-probar-btn">
                              {probando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PlugZap className="w-4 h-4 mr-2" />}
                              Probar conexión
                            </Button>
                          </div>
                          {config.ultimo_chequeo && (
                            <p className="text-xs text-slate-400">Último chequeo: {fmtFecha(config.ultimo_chequeo)}</p>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
                    <CardHeader>
                      <CardTitle className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                        <Clock className="w-4 h-4 text-blue-600" /> Últimas corridas
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2" data-testid="runs-list">
                      {runs.length === 0 ? (
                        <p className="text-sm text-slate-400">Sin corridas registradas</p>
                      ) : runs.map((r) => (
                        <div key={r.id} className="p-2.5 rounded-lg border border-slate-100 dark:border-slate-700 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-slate-700 dark:text-slate-200">{fmtFecha(r.inicio)}</span>
                            <Badge className={r.estado === "completado" ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" : r.estado === "error" ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" : "bg-amber-100 text-amber-800"}>
                              {r.estado}
                            </Badge>
                          </div>
                          <p className="text-slate-500 dark:text-slate-400 mt-1">
                            {r.disparo === "programado" ? "Programada" : "Manual"} · {r.correos_nuevos ?? 0} correos nuevos · {r.adjuntos_pdf ?? 0} PDFs · {r.hallazgos_nuevos ?? 0} hallazgos
                          </p>
                          {(r.errores || []).length > 0 && (
                            <p className="text-red-500 mt-1 truncate" title={r.errores.join("; ")}>⚠ {r.errores[0]}</p>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            )}
          </Tabs>
        </div>
      </main>
    </div>
  );
}
